import jwtDecode from 'jwt-decode';
import mondaySdk from 'monday-sdk-js';
import Button from 'monday-ui-react-core/dist/Button';
import IconButton from 'monday-ui-react-core/dist/IconButton';
import Close from 'monday-ui-react-core/dist/icons/Close';
import SettingsIcon from 'monday-ui-react-core/dist/icons/Settings';
import ToolTip from 'monday-ui-react-core/dist/Tooltip';
import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useMatch, useNavigate } from 'react-router-dom';
import { darkThemeValues as darkThemeConfig } from '../../constants/theme';
import {
  getBoardPermission,
  getFileDetails,
  mondayGetItem,
} from '../../services/monday';
import { getUploadedTemplate } from '../../services/uploadFile';
import { getContext } from '../../utils/http';
import HeaderContainer from './HeaderContainer';
const monday = mondaySdk();

const Header = () => {
  const navigate = useNavigate();
  const homePage = useMatch('/');
  const queryClient = useQueryClient();
  const applicationStatus = useMatch('/application-status/*');
  const workflowSelection = useMatch('/workflow-selection');
  const uploadPage = useMatch('/upload-template');
  const adhocUploadPage = useMatch('/adhoc-upload-file');
  const adhocStatus = useMatch('/adhoc-application-status');
  const AdhocSettings = useMatch('/adhoc-settings');
  const generateTemplatesPage = useMatch('/generate/templates');
  const uploadTemplateForPDFGen = useMatch('/generate/upload-template');
  const [darkTheme, setDarkTheme] = useState(false);

  const sessionToken = queryClient.getQueryData('session-token');

  const decoded = sessionToken?.data
    ? jwtDecode(sessionToken?.data)
    : undefined;

  monday.listen('context', (res) => {
    if (darkThemeConfig.includes(res?.data?.theme)) {
      setDarkTheme(true);
    } else {
      setDarkTheme(false);
    }
  });

  const { data: context, isLoading: isLoadingContext } = useQuery(
    'get-context',
    getContext,
  );

  const { data: hasPermission, isLoading: isLoadingPermission } = useQuery(
    'get-permission',
    () => getBoardPermission(context?.boardId, context?.user.id),
    {
      enabled: !!context?.boardId && !!context?.user.id,
    },
  );

  const { data: boardTemplate, isError } = useQuery(
    'get-board-template',
    () =>
      getUploadedTemplate(
        context?.boardId,
        context?.itemId,
        context?.instanceId,
      ),
    {
      enabled: !!context?.boardId && !!context.itemId,
      retry: false,
      refetchOnWindowFocus: 'always',
      onError: (err) => {
        if (err?.response?.status === 401) {
          queryClient.setQueryData('get-board-template', {
            data: {
              alreadySignedFile: false,
              doc: null,
              hasStartedSigningProcess: false,
              isAuthenticated: false,
            },
          });
          if (!homePage) navigate('/');
        }
      },
    },
  );

  const { data: preSignedFile, isLoading: isLoadingPreSignedFile } = useQuery(
    'get-pre-signed-file-from-monday',
    () =>
      getFileDetails({
        boardId: context?.boardId,
        itemId: context?.itemId,
        columnId: boardTemplate?.data?.doc?.presigned_file_column_id,
      }),
    {
      refetchOnWindowFocus: false,
      enabled:
        !!boardTemplate?.data?.doc && !!context?.boardId && !!context?.itemId,
      onSuccess: async (res) => {},
    },
  );

  const handleClose = async () => {
    if (adhocUploadPage) {
      navigate('/adhoc-settings');
      return;
    }
    if (boardTemplate?.data?.doc?.type === 'adhoc') {
      if (adhocUploadPage || adhocStatus || adhocUploadPage) {
        navigate('/adhoc-settings');
        return;
      }

      if (AdhocSettings) {
        if (
          !preSignedFile?.data?.boards?.[0]?.items_page.items?.[0]?.column_values?.[0]?.text
            ?.toLowerCase?.()
            ?.endsWith('.pdf')
        ) {
          return navigate('/adhoc-upload-file', { replace: true });
        } else {
          return navigate('/adhoc-application-status', { replace: true });
        }
      }

      return navigate('/adhoc-application-status');
    }

    if (uploadPage || uploadTemplateForPDFGen) {
      return navigate('/workflow-selection');
    }

    if (boardTemplate?.data?.doc?._id) {
      navigate('/application-status/' + boardTemplate?.data?.doc?._id, {
        state: null,
      });
      return;
    }

    const workflow = await mondayGetItem('workflowType');
    if (workflow?.data?.value === 'adhoc') {
      navigate('/workflow-selection', { state: null });
      return;
    }

    navigate('/upload-template', { state: null });
  };

  const handleSettingsClick = async () => {
    const workflow = await mondayGetItem('workflowType');
    if (workflow?.data?.value === 'adhoc') {
      navigate('/adhoc-settings', { state: null });
      return;
    }
    if (boardTemplate?.data?.doc?._id) {
      navigate('/application-details');
    } else {
      navigate('/upload-template');
    }
  };

  return (
    <HeaderContainer>
      {boardTemplate?.data?.isAuthenticated &&
      !isError &&
      !workflowSelection &&
      !generateTemplatesPage ? (
        homePage || applicationStatus || adhocStatus || adhocUploadPage ? (
          <>
            {!isLoadingPermission && hasPermission ? (
              <Button
                className="dark:text-white"
                kind={Button.kinds.SECONDARY}
                leftIcon={SettingsIcon}
                onClick={handleSettingsClick}
                disabled={
                  boardTemplate?.data?.doc?.is_deleted ||
                  decoded?.dat?.is_view_only
                }
              >
                Settings
              </Button>
            ) : (
              <ToolTip
                content={
                  'Only board owners are allowed to update the settings.'
                }
              >
                <Button
                  className="dark:!text-white dark:!opacity-50"
                  kind={Button.kinds.SECONDARY}
                  leftIcon={SettingsIcon}
                  disabled
                >
                  Settings
                </Button>
              </ToolTip>
            )}
          </>
        ) : (
          <IconButton
            ariaLabel="Close"
            icon={Close}
            onClick={handleClose}
            color={darkTheme ? IconButton.colors.ON_INVERTED_BACKGROUND : ''}
          />
        )
      ) : null}
    </HeaderContainer>
  );
};

export default Header;
